import React from "react";
import { Button } from "@/components/ui/button";
import { isDemoMode } from "@/components/DemoHelpers";

const priceOrHidden = (n) => (isDemoMode() ? "€ ***" : `€ ${Number(n || 0).toFixed(2)}`);

/**
 * Stage "chained_prereq": selezione prerequisiti a catena (acquista con questo o già in possesso).
 */
export default function ChainedPrereqStage({
  selected,
  chainedServicesDetails,
  chainedChoices,
  onSetChoice,
  onBack,
  onNext,
}) {
  const allChosen = chainedServicesDetails.every((cs) => chainedChoices[cs._id]);
  const primaryPrice = selected.professional_price || 0;
  const chainedTotal = chainedServicesDetails.reduce(
    (acc, cs) => (chainedChoices[cs._id] === "purchase" ? acc + (cs.professional_price || 0) : acc),
    0,
  );
  const total = primaryPrice + chainedTotal;

  return (
    <div className="space-y-4 py-2" data-testid="stage-chained-prereq">
      <div className="p-3 rounded-lg bg-purple-50 border border-purple-200">
        <p className="text-sm text-purple-900 font-medium">
          Per svolgere <strong>{selected.name}</strong> è necessario che tu abbia i seguenti documenti/servizi.
        </p>
        <p className="text-xs text-purple-700 mt-1">
          Se già li possiedi, seleziona "Già in mio possesso". Altrimenti, aggiungili all'acquisto: verranno sommati al
          totale.
        </p>
      </div>
      <div className="space-y-2">
        {chainedServicesDetails.map((cs) => {
          const choice = chainedChoices[cs._id];
          return (
            <div
              key={cs._id}
              className={`p-3 border-2 rounded-lg transition-colors ${choice ? "border-purple-300 bg-purple-50/30" : "border-slate-200"}`}
              data-testid={`chained-row-${cs._id}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex-1">
                  <div className="font-semibold text-sm">{cs.name}</div>
                  <div className="text-xs text-muted-foreground">{cs.category}</div>
                </div>
                <div className="text-lg font-bold text-purple-700 font-mono">
                  {priceOrHidden(cs.professional_price)}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => onSetChoice(cs._id, "purchase")}
                  className={`px-3 py-2 rounded text-xs font-semibold border-2 transition-all ${choice === "purchase" ? "ht-gradient border-0 text-white" : "bg-white border-slate-200 hover:border-purple-400"}`}
                  data-testid={`chained-buy-${cs._id}`}
                >
                  🛒 Acquista con questo servizio
                </button>
                <button
                  type="button"
                  onClick={() => onSetChoice(cs._id, "owned")}
                  className={`px-3 py-2 rounded text-xs font-semibold border-2 transition-all ${choice === "owned" ? "bg-emerald-100 border-emerald-500 text-emerald-800" : "bg-white border-slate-200 hover:border-emerald-400"}`}
                  data-testid={`chained-owned-${cs._id}`}
                >
                  ✓ Già in mio possesso
                </button>
              </div>
            </div>
          );
        })}
      </div>
      <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 space-y-1">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">{selected.name}</span>
          <span className="font-mono">{priceOrHidden(primaryPrice)}</span>
        </div>
        {chainedServicesDetails
          .filter((cs) => chainedChoices[cs._id] === "purchase")
          .map((cs) => (
            <div key={cs._id} className="flex justify-between text-xs text-purple-700">
              <span>+ {cs.name}</span>
              <span className="font-mono">{priceOrHidden(cs.professional_price)}</span>
            </div>
          ))}
        <div className="flex justify-between pt-2 border-t border-slate-300 font-bold">
          <span>Totale</span>
          <span className="font-mono text-purple-800" data-testid="chained-total">
            {priceOrHidden(total)}
          </span>
        </div>
      </div>
      {!allChosen && (
        <p className="text-xs text-amber-700 text-center">
          ⚠️ Fai una scelta per ogni prerequisito per proseguire.
        </p>
      )}
      <div className="flex gap-2">
        <Button variant="outline" onClick={onBack} data-testid="chained-back">
          ← Indietro
        </Button>
        <Button
          className="flex-1 ht-gradient border-0"
          disabled={!allChosen}
          onClick={onNext}
          data-testid="chained-next"
        >
          Continua →
        </Button>
      </div>
    </div>
  );
}
